import { PageLayout } from '../../../core/types/Typesetter';
import { findCacheKeysToInvalidate, findPageIndicesToInvalidate } from './CacheKeyFilter';
import { cachePage } from './PageCacheManager';
import { buildParagraphToPageMap } from '../paragraph';
import { updateTotalPageCount } from '../pagination';
import { getMinPageIndex } from '../pagination';

// Debug flag - set to false for production
const DEBUG_ENABLED = false;

/**
 * PageCacheMerger - Utility for merging incremental pagination results into the page cache
 *
 * Handles cache invalidation, page storage, and mapping updates when new pages
 * are generated from incremental pagination operations.
 */
export class PageCacheMerger {
  /**
   * Merge new pages into the page cache after incremental pagination
   *
   * This method performs the following operations:
   * 1. Identifies the starting page index from new pages
   * 2. Invalidates existing cached pages from that index forward
   * 3. Removes corresponding page hashes
   * 4. Adds new pages to the cache
   * 5. Updates paragraph-to-page mappings
   * 6. Updates total page count
   *
   * @param newPages - Array of PageLayout objects from incremental pagination
   * @param spillFrontier - SpillFrontier cursor indicating where pagination resumed
   * @param pageCache - The page cache map (key: cacheKey, value: PageLayout)
   * @param currentDocHash - Current document hash for cache key generation
   * @param pageHashes - Map of page indices to page hashes
   * @param currentTotalPages - Current total page count (will be updated)
   * @returns Updated total page count
   */
  static mergePagesIntoCache(
    newPages: PageLayout[],
    spillFrontier: { para_ix: number; line_ix: number },
    pageCache: Map<string, PageLayout>,
    currentDocHash: string,
    pageHashes: Map<number, string>,
    currentTotalPages: number
  ): number {
    if (newPages.length === 0) {
      return currentTotalPages;
    }

    // Find first page index in new pages
    const startPageIndex = getMinPageIndex(newPages) ?? 0;

    DEBUG_ENABLED && console.log(`📊 [PageCacheMerger] Merging pages into cache:`, {
      startPageIndex,
      newPagesCount: newPages.length,
      spillFrontier
    });

    // Remove cached pages from startPageIndex forward
    const keysToDelete = findCacheKeysToInvalidate(pageCache, currentDocHash, startPageIndex);

    for (const key of keysToDelete) {
      pageCache.delete(key);
    }

    // Remove hashes for deleted pages
    const indicesToDelete = findPageIndicesToInvalidate(pageHashes, startPageIndex);
    for (const pageIndex of indicesToDelete) {
      pageHashes.delete(pageIndex);
    }

    DEBUG_ENABLED && console.log(`📊 [PageCacheMerger] Removed ${keysToDelete.length} cached pages from index ${startPageIndex}`);

    // Add new pages to cache
    for (const page of newPages) {
      cachePage(pageCache, currentDocHash, page);

      if (page.page_hash) {
        pageHashes.set(page.page_index, page.page_hash);
      }
    }

    DEBUG_ENABLED && console.log(`📊 [PageCacheMerger] Added ${newPages.length} pages to cache`);

    // Update paragraph-to-page mapping for new pages
    buildParagraphToPageMap(newPages);

    // Update total pages count
    return updateTotalPageCount(currentTotalPages, newPages);
  }
}
